import React, { useState, useEffect } from "react";

import { IColumnOption } from "../../constants/registrations-columns";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";

import "./ColumnSelectionModal.scss";

interface IColumnSelectionModalProps {
  onConfirm: (selectedColumns: string[]) => void;
  onCancel: () => void;
  columns: IColumnOption[];
  exportName: string;
}

export const ColumnSelectionModal: React.FC<IColumnSelectionModalProps> = (props) => {
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Initialize with default selections
    const defaultSelected = props.columns
      .filter(col => col.defaultSelected)
      .map(col => col.key);
    setSelectedColumns(new Set(defaultSelected));
  }, [props.columns]);

  const handleToggleColumn = (columnKey: string, isMandatory: boolean) => {
    if (isMandatory) {
      // Cannot unselect mandatory columns
      return;
    }

    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allColumns = props.columns.map(col => col.key);
    setSelectedColumns(new Set(allColumns));
  };

  const handleDeselectAll = () => {
    // Keep only mandatory columns
    const mandatoryColumns = props.columns
      .filter(col => col.mandatory)
      .map(col => col.key);
    setSelectedColumns(new Set(mandatoryColumns));
  };

  const handleConfirm = () => {
    // Always include mandatory columns
    const mandatoryColumns = props.columns
      .filter(col => col.mandatory)
      .map(col => col.key);
    
    const finalSelection = Array.from(new Set([
      ...mandatoryColumns,
      ...Array.from(selectedColumns)
    ]));

    props.onConfirm(finalSelection);
  };

  const mandatoryColumns = props.columns.filter(col => col.mandatory);
  const optionalColumns = props.columns.filter(col => !col.mandatory);

  return (
    <div className="ColumnSelectionModal">
      <h2>Export {props.exportName}</h2>
      <p>Select columns to include in the export:</p>

      <div className="scrollable-content">
        {/* Mandatory Columns */}
        {mandatoryColumns.length > 0 && (
          <div className="column-section">
            {mandatoryColumns.map(column => (
              <div key={column.key} className="column-option-wrapper">
                <Checkbox
                  id={`col-${column.key}`}
                  label={column.label}
                  value={true}
                  onChange={() => {}}
                  disabled={true}
                  className="column-checkbox"
                />
                <span className="required-badge">REQUIRED</span>
              </div>
            ))}
          </div>
        )}

        {/* Separator */}
        {mandatoryColumns.length > 0 && optionalColumns.length > 0 && (
          <div className="column-separator"></div>
        )}

        {/* Optional Columns */}
        {optionalColumns.length > 0 && (
          <div className="column-section">
            {optionalColumns.map(column => (
              <div key={column.key} className="column-option-wrapper">
                <Checkbox
                  id={`col-${column.key}`}
                  label={column.label}
                  value={selectedColumns.has(column.key)}
                  onChange={() => handleToggleColumn(column.key, column.mandatory)}
                  className="column-checkbox"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="modal-actions">
        <div className="select-buttons">
          <button className="link-button" onClick={handleSelectAll}>
            Select All
          </button>
          <button className="link-button" onClick={handleDeselectAll}>
            Deselect All
          </button>
        </div>

        <div className="confirm-buttons">
          <Button className="secondary" onClick={props.onCancel}>
            Cancel
          </Button>
          <Button className="primary" onClick={handleConfirm}>
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

